import React, { FC, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Typography, Table, TopProgress, BackArrow, InfoBlock } from "../../../../components";
import { Container } from './styles'
import { useQueryParam, StringParam, NumberParam } from 'use-query-params';
import { affiliateService } from "../../../../api";
import { useParams, useNavigate } from "react-router-dom";
import { timeConverterMMDDYYHHMM } from "../../../../utils/dates";
import { useUi } from "../../../../context/ui.context";
import {
  StateWrapper,
  GreenDot,
  Greentext
} from "../../../Instances/styles";
import { handleStatusName } from "../../../../utils/handle-status-name";


const CustomerPage: FC = () => {
  const { customerId } = useParams()
  const { viewAlert } = useUi()
  const [t] = useTranslation()
  const navigate = useNavigate()
  const [customer, setCustomer] = useState<any>(null)
  const [loading, setLoading] = useState<boolean>(true)

  const [page, setPage] = useQueryParam('page', NumberParam);
  const [ordering, setOrdering] = useQueryParam('ordering', StringParam);
  const [pageSize, setPageSize] = useQueryParam('page_size', NumberParam);
  const [paginationData, setPaginationData] = useState<any>()
  const [conversions, setConversions] = useState<any[]>([])
  const [conversionsLoading, setConversionsLoading] = useState<boolean>(false)

  const getCustomerData = async () => {
    try {
      setLoading(true)
      const response = await affiliateService.getCustomer(customerId!)
      setCustomer(response?.data)
    } catch (e: any) {
      viewAlert({
        severity: 'error',
        message: e?.response?.data?.detail || t("ERROR_OCCURRED")
      })
    } finally {
      setLoading(false)
    }
  }

  const getConversionsData = async (params?: any) => {
    try {
      setConversionsLoading(true)
      const response = await affiliateService.getConversions({
        ...params,
        referral_id: customerId,
        page: params?.page || page || 1,
        page_size: params?.page_size || pageSize || 10,
        ordering: params?.ordering || ordering
      })
      const data = response?.data

      if (data) {
        setPaginationData({
          count: data.count || 0,
          total_pages: data.total_pages || 0,
          pageNo: data.current_page || 1,
          page_size: data.page_size || 10
        })

        const formattedConversions = data.results?.map((transaction: any) => ({
          ...transaction,
          amount: `${parseFloat(transaction.amount).toFixed(2)} ${transaction.currency}`,
          customer_id: transaction.referral?.user?.id || 'N/A',
          country: transaction.referral?.client?.country || transaction.referral?.region || 'unknown',
          created: timeConverterMMDDYYHHMM(transaction.created_at),
          navigate: `/affiliate/conversions/${transaction.id}`,
        })) || []

        setConversions(formattedConversions)
      }
    } catch (e: any) {
      console.error('Error fetching conversions:', e)
    } finally {
      setConversionsLoading(false)
    }
  }

  useEffect(() => {
    getCustomerData()
  }, [customerId])

  useEffect(() => {
    if (customerId) {
      getConversionsData()
    }
  }, [customerId, page, pageSize, ordering])

  const ConversionColumns = [
    {
      title: t("EXTERNAL_ID"),
      key: 'id'
    },
    {
      title: t("CREATED_AT"),
      key: 'created'
    },
    {
      title: t("CONVERSION_AMOUNT"),
      key: 'amount'
    },
    {
      title: t("COUNTRY"),
      key: 'country'
    },
  ]

  if (loading) return <TopProgress loading={loading} />

  const customerInfoData = customer ? [
    {
      title: t("STATUS"),
      value: '',
      custom: (
        <StateWrapper>
          <GreenDot active={customer.status === 'approved' ? 'true' : 'false'} />
          <Greentext active={customer.status === 'approved' ? 'true' : 'false'}>
            {handleStatusName(customer.status, t)}
          </Greentext>
        </StateWrapper>
      )
    },
    {
      title: t("EMAIL"),
      value: customer.user?.email || '-',
      copy: true
    },
    {
      title: t("CUSTOMER_ID"),
      value: customer.user?.id || '-'
    },
    {
      title: t("REGION"),
      value: customer.region?.toUpperCase() || '-'
    },
    {
      title: t("CREATED_AT"),
      value: timeConverterMMDDYYHHMM(customer.created_at)
    },
  ] : []

  return (
    <Container>
      <BackArrow
        title={t("BACK_TO_CUSTOMERS")}
        onClick={() => navigate('/affiliate/customers')}
      />
      {customer && (
        <InfoBlock
          title={customer.user?.full_name || t("CUSTOMER")}
          data={customerInfoData}
        />
      )}

      <Typography variant="h2">
        {t("CONVERSIONS")}
      </Typography>
      {conversionsLoading ?
        <TopProgress loading={conversionsLoading} />
        : conversions.length === 0 ?
          <Typography variant="body">{t("THERE_ARE_NO_CONVERSIONS_YET")}</Typography>
          :
          <Table
            columns={ConversionColumns}
            rows={conversions}
            paginationData={paginationData}
            hideCheckbox
            updateData={getConversionsData}
            loading={conversionsLoading}
            customSetPage={setPage}
            customSetPageSize={setPageSize}
            customPageSize={pageSize}
          />
      }
    </Container>
  )
}

export default CustomerPage
